import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthTokenService } from '../services/auth.token.service';
import { AccessTokenResponse } from '../models/swagger/accessTokenResponse';

@Injectable({
    providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(private router: Router, private authTokenService: AuthTokenService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let accessTokenResponse: AccessTokenResponse | undefined = this.getUserToken()

        if (accessTokenResponse && accessTokenResponse.accessToken) {
            request = this.addTokenHeader(request, accessTokenResponse.accessToken)
        }

        return next.handle(request).pipe(
            catchError(
                error => {
                    if ([401].includes(error.status)) {
                        if (request.url.endsWith('/auth/refresh')) {
                            this.isRefreshing = false;
                            this.authTokenService.removeAuth();
                            this.router.navigate(['/base/login']);
                        } else {
                            return this.handleUnauthorized(request, next);
                        }
                    }

                    return throwError(() => error);
                }
            )
        );
    }

    private getUserToken(): AccessTokenResponse | undefined {
        return this.authTokenService.getAuth()
    }

    private addTokenHeader(request: HttpRequest<any>, token: String) {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            let token: AccessTokenResponse | undefined = this.getUserToken();

            if (!token || !token.accessToken || !token.refreshToken) {
                this.isRefreshing = false;
                this.authTokenService.removeAuth();
                this.router.navigate(['/base/login']);
            }

            return this.authTokenService.refreshToken({ refreshToken: token.refreshToken }).pipe(
                switchMap((accessTokenResponse: AccessTokenResponse) => {
                    this.isRefreshing = false;
                    this.authTokenService.registerAuth(accessTokenResponse);
                    this.refreshTokenSubject.next(accessTokenResponse.accessToken);

                    const clonedRequest = this.addTokenHeader(request, accessTokenResponse.accessToken)
                    return next.handle(clonedRequest);
                }),
                catchError((error: any) => {
                    this.isRefreshing = false;
                    this.authTokenService.removeAuth();
                    this.router.navigate(['/base/login']);

                    return throwError(() => error);
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap((token: string | null) => {
                    return next.handle(this.addTokenHeader(request, token!));
                })
            );
        }
    }
}